import { useEffect, useMemo } from 'react';
import { useSiteSettings } from '../hooks/use-site-settings';

const ensureMetaTag = (attribute, key) => {
  const selector = `meta[${attribute}="${key}"]`;
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  return element;
};

const toAbsoluteUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }

  try {
    return new URL(raw, window.location.origin).toString();
  } catch (error) {
    return '';
  }
};

const SiteMeta = () => {
  const { siteSettings } = useSiteSettings();

  const title = useMemo(() => {
    const name = String(siteSettings.businessName || '').trim();
    const tagline = String(siteSettings.businessTagline || '').trim();

    if (name && tagline) {
      return `${name} | ${tagline}`;
    }

    return name || 'Wellness & Coaching';
  }, [siteSettings.businessName, siteSettings.businessTagline]);

  const excerpt = useMemo(
    () =>
      String(
        siteSettings.siteExcerpt ||
          siteSettings.footerDescription ||
          'Professional wellness and coaching services.',
      ).trim(),
    [siteSettings.footerDescription, siteSettings.siteExcerpt],
  );

  const imageUrl = useMemo(
    () => toAbsoluteUrl(siteSettings.featuredImageUrl),
    [siteSettings.featuredImageUrl],
  );

  useEffect(() => {
    if (title) {
      document.title = title;
    }

    const descriptionTag = ensureMetaTag('name', 'description');
    const ogTitleTag = ensureMetaTag('property', 'og:title');
    const ogDescriptionTag = ensureMetaTag('property', 'og:description');
    const ogTypeTag = ensureMetaTag('property', 'og:type');
    const ogImageTag = ensureMetaTag('property', 'og:image');
    const twitterCardTag = ensureMetaTag('name', 'twitter:card');
    const twitterTitleTag = ensureMetaTag('name', 'twitter:title');
    const twitterDescriptionTag = ensureMetaTag('name', 'twitter:description');
    const twitterImageTag = ensureMetaTag('name', 'twitter:image');

    descriptionTag.setAttribute('content', excerpt);
    ogTitleTag.setAttribute('content', title);
    ogDescriptionTag.setAttribute('content', excerpt);
    ogTypeTag.setAttribute('content', 'website');
    twitterTitleTag.setAttribute('content', title);
    twitterDescriptionTag.setAttribute('content', excerpt);

    if (imageUrl) {
      ogImageTag.setAttribute('content', imageUrl);
      twitterImageTag.setAttribute('content', imageUrl);
      twitterCardTag.setAttribute('content', 'summary_large_image');
    } else {
      ogImageTag.setAttribute('content', '');
      twitterImageTag.setAttribute('content', '');
      twitterCardTag.setAttribute('content', 'summary');
    }
  }, [excerpt, imageUrl, title]);

  return null;
};

export default SiteMeta;
